import { useEffect, useRef, useState } from 'react';
import { registerHandle } from '../../app/handles';

/**
 * The "Saved" toast.
 *
 * Tiny on purpose: it is the first consumer of the bridge, and its job here is to prove that
 * an EVENT crosses the seam cleanly — the About panel proved state and rendering, but nothing
 * had yet been triggered by the vanilla app at a moment of its choosing.
 *
 * It reuses the existing `.saved` / `.saved.show` CSS, like every region in this phase: the
 * opacity-and-translate transition lives there, and reimplementing it in React would change
 * the animation while claiming to change nothing.
 *
 * Note what the vanilla version got right and this keeps: repeated saves RESTART the timer
 * rather than queueing, so a burst of edits shows one toast that lingers, not a stutter.
 */

/** How long the toast stays up after the last save. Matches the vanilla 900ms exactly. */
const LINGER_MS = 900;

export interface SavedToastHandle {
  show: () => void;
}

export function SavedToast({ host }: { host: HTMLElement }) {
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The host carries the CSS, including the transition — so the class has to land on it
  // rather than on anything React renders inside. Same arrangement as the About panel.
  useEffect(() => {
    host.classList.toggle('show', visible);
  }, [visible, host]);

  useEffect(() => {
    const handle: SavedToastHandle = {
      show: () => {
        setVisible(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setVisible(false), LINGER_MS);
      },
    };
    registerHandle('toast', handle);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // The text is the whole component. It stays rendered rather than being conditional,
  // because the CSS animates opacity — unmounting it would make the toast disappear
  // instantly instead of fading.
  return <>Saved</>;
}
